import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { 
  ArrowLeftIcon, 
  DocumentArrowUpIcon,
  CheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useCart } from '../contexts/CartContext';
import { ordersAPI, uploadAPI } from '../utils/api';
import { CreateOrderData } from '../types';

interface CheckoutFormData {
  customerName: string;
  email: string;
  phone: string;
  address: string;
  deliveryMethod: 'Pickup' | 'Courier';
  notes?: string;
}

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const { items, total, itemCount, clearCart } = useCart();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [proofOfPayment, setProofOfPayment] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<CheckoutFormData>({
    defaultValues: {
      deliveryMethod: 'Pickup'
    }
  });

  const deliveryMethod = watch('deliveryMethod');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a JPG, PNG, or PDF file');
        return;
      }
      
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      
      setProofOfPayment(file);
      setError(null);
    }
  };

  const onSubmit = async (data: CheckoutFormData) => {
    if (!proofOfPayment) {
      setError('Please upload your proof of payment');
      return;
    }

    if (items.length === 0) {
      setError('Your cart is empty');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Upload proof of payment first
      const uploadFormData = new FormData();
      uploadFormData.append('proofOfPayment', proofOfPayment);

      const uploadResponse = await uploadAPI.uploadProofOfPayment(proofOfPayment);
      
      if (!uploadResponse.data.success) {
        throw new Error('Failed to upload proof of payment');
      }

      // Create order
      const orderData: CreateOrderData = {
        customerName: data.customerName,
        email: data.email,
        phone: data.phone,
        address: data.address,
        deliveryMethod: data.deliveryMethod,
        products: items.map(item => ({
          productId: item.productId,
          quantity: item.quantity
        })),
        notes: data.notes
      };

      const orderResponse = await ordersAPI.createOrder(orderData);
      
      if (orderResponse.data.success && orderResponse.data.data) {
        setSuccess(true);
        setOrderNumber(orderResponse.data.data.order.orderNumber);
        clearCart();
      } else {
        throw new Error(orderResponse.data.message || 'Failed to create order');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0 && !success) {
    navigate('/cart');
    return null;
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckIcon className="h-8 w-8 text-green-600" />
            </div>
            
            <h1 className="text-3xl font-bold text-primary mb-4">Order Confirmed!</h1>
            <p className="text-lg text-text mb-6">
              Thank you for your order. We have received your request and will process it once we verify your payment.
            </p>
            
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h2 className="text-xl font-semibold text-primary mb-4">Order Details</h2>
              <div className="space-y-2 text-left">
                <div className="flex justify-between">
                  <span className="text-text">Order Number:</span>
                  <span className="font-semibold text-primary">{orderNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text">Total Amount:</span>
                  <span className="font-semibold text-primary">${total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text">Items:</span>
                  <span className="font-semibold text-primary">{itemCount}</span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">What's Next?</h3>
              <div className="space-y-2 text-left text-blue-800">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <span className="text-xs font-semibold">1</span>
                  </div>
                  <span>We will verify your proof of payment within 24 hours</span>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <span className="text-xs font-semibold">2</span>
                  </div>
                  <span>Once verified, we will begin processing your order</span>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                    <span className="text-xs font-semibold">3</span>
                  </div>
                  <span>You will receive email updates on your order status</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/shop')}
                className="btn-primary px-8 py-3"
              >
                Continue Shopping
              </button>
              <button
                onClick={() => window.print()}
                className="btn-outline px-8 py-3"
              >
                Print Receipt
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate('/cart')}
            className="flex items-center text-primary hover:text-accent transition-colors duration-200 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Cart
          </button>
          <h1 className="text-3xl font-bold text-primary">Checkout</h1>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h2 className="text-xl font-semibold text-primary mb-6">Customer Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    {...register('customerName', { required: 'Full name is required' })}
                    className="input-field"
                    placeholder="Enter your full name"
                  />
                  {errors.customerName && (
                    <p className="mt-1 text-sm text-red-600">{errors.customerName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    className="input-field"
                    placeholder="Enter your email"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    {...register('phone', { required: 'Phone number is required' })}
                    className="input-field"
                    placeholder="Enter your phone number"
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Delivery Method *
                  </label>
                  <select
                    {...register('deliveryMethod', { required: 'Delivery method is required' })}
                    className="input-field"
                  >
                    <option value="Pickup">Pickup</option>
                    <option value="Courier">Courier Delivery</option>
                  </select>
                  {errors.deliveryMethod && (
                    <p className="mt-1 text-sm text-red-600">{errors.deliveryMethod.message}</p>
                  )}
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {deliveryMethod === 'Pickup' ? 'Pickup Address' : 'Delivery Address'} *
                </label>
                <textarea
                  {...register('address', { required: 'Address is required' })}
                  rows={3}
                  className="input-field"
                  placeholder={deliveryMethod === 'Pickup' 
                    ? 'Enter pickup address' 
                    : 'Enter delivery address'
                  }
                />
                {errors.address && (
                  <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
                )}
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  {...register('notes')}
                  rows={3}
                  className="input-field"
                  placeholder="Any special instructions or notes..."
                />
              </div>
            </div>

            {/* Proof of Payment */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-primary mb-6">Proof of Payment</h2>
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <DocumentArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Upload your proof of payment
                </h3>
                <p className="text-gray-600 mb-4">
                  Please upload a screenshot or photo of your bank transfer, Ecocash payment, or any other payment confirmation.
                </p>
                
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept="image/jpeg,image/jpg,image/png,application/pdf"
                  className="hidden"
                  id="proof-of-payment"
                />
                <label
                  htmlFor="proof-of-payment"
                  className="btn-primary cursor-pointer inline-block px-6 py-3"
                >
                  Choose File
                </label>
                
                {proofOfPayment && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center text-green-800">
                      <CheckIcon className="h-5 w-5 mr-2" />
                      <span className="font-medium">{proofOfPayment.name}</span>
                    </div>
                    <p className="text-sm text-green-600 mt-1">
                      {(proofOfPayment.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">Payment Instructions:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Make payment to: +263 774 346 061 (Ecocash)</li>
                      <li>Or bank transfer to our account (contact us for details)</li>
                      <li>Upload proof of payment (screenshot, receipt, or confirmation)</li>
                      <li>We will verify payment within 24 hours</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-24">
              <h2 className="text-lg font-semibold text-primary mb-4">Order Summary</h2>
              
              <div className="space-y-3 mb-6">
                {items.map((item) => (
                  <div key={item.productId} className="flex justify-between text-sm">
                    <span className="text-text">
                      {item.name} × {item.quantity}
                    </span>
                    <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <hr className="border-gray-200" />
                <div className="flex justify-between text-lg font-semibold text-primary">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !proofOfPayment}
                className={`w-full py-4 px-6 rounded-lg font-medium transition-colors duration-200 ${
                  loading || !proofOfPayment
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-primary text-white hover:bg-primary-dark'
                }`}
              >
                {loading ? 'Processing...' : 'Place Order'}
              </button>

              <p className="text-xs text-gray-500 mt-3 text-center">
                By placing this order, you agree to our terms and conditions.
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
